import { isScheduledBook } from "@book-club/shared";
import type { SortColumn } from "@/features/books/constants/sortColumns";
import type { BookWithStats, FilterType } from "@/features/books/types";

/**
 * Checks if a specificBook matches the search query
 */
function matchesSpecificBook(book: BookWithStats, query: string): boolean {
    const titleMatch = book.title?.toLowerCase().includes(query) ?? false;
    const authorMatch = book.author?.toLowerCase().includes(query) ?? false;
    return titleMatch || authorMatch;
}

/**
 * Checks if an authorFocus book matches the search query
 */
function matchesAuthorFocus(book: BookWithStats, query: string): boolean {
    const authorMatch = book.author?.toLowerCase().includes(query) ?? false;
    return authorMatch || "author focus".includes(query);
}

/**
 * Checks if a freeChoice book matches the search query
 */
function matchesFreeChoice(query: string): boolean {
    return "free choice".includes(query);
}

/**
 * Filters books by search query (title or author)
 */
export function filterBooks(books: BookWithStats[], searchQuery: string): BookWithStats[] {
    if (!searchQuery.trim()) {
        return books;
    }

    const query = searchQuery.toLowerCase();
    return books.filter((book) => {
        if (book.type === "specificBook") {
            return matchesSpecificBook(book, query);
        }
        if (book.type === "authorFocus") {
            return matchesAuthorFocus(book, query);
        }
        if (book.type === "freeChoice") {
            return matchesFreeChoice(query);
        }
        if (book.type === "tba") {
            return "to be announced".includes(query);
        }
        return false;
    });
}

/**
 * Gets the title sort value for a book
 */
function getTitleSortValue(book: BookWithStats): string {
    if (book.type === "specificBook") {
        return book.title ?? "";
    }
    if (book.type === "authorFocus") {
        return "Author Focus";
    }
    if (book.type === "freeChoice") {
        return "Free Choice";
    }
    if (book.type === "tba") {
        return "To Be Announced";
    }
    return "";
}

/**
 * Gets the author sort value for a book
 */
function getAuthorSortValue(book: BookWithStats): string {
    if (book.type === "specificBook" || book.type === "authorFocus") {
        return book.author ?? "";
    }
    return "";
}

/**
 * Gets the sort value for a book based on the sort column
 */
function getSortValue(book: BookWithStats, sortColumn: SortColumn): string | number {
    switch (sortColumn) {
        case "title":
            return getTitleSortValue(book);
        case "author":
            return getAuthorSortValue(book);
        case "featuredDate":
            // Sort by date string (nulls go to end)
            return book.featuredDate ?? "";
        case "rating":
            // Sort by average rating (nulls go to end)
            return book.averageRating ?? -1;
        case "createdAt":
            // Sort by timestamp
            return book.createdAt.getTime();
    }
}

/**
 * Checks if a value represents a null/empty value for sorting
 */
function isNullValue(value: string | number, sortColumn: SortColumn): boolean {
    if (sortColumn === "featuredDate") {
        return value === "";
    }
    if (sortColumn === "rating") {
        return value === -1;
    }
    return false;
}

/**
 * Compares two values and returns sort result
 */
function compareValues(aValue: string | number, bValue: string | number, sortDirection: "asc" | "desc"): number {
    if (aValue < bValue) {
        return sortDirection === "asc" ? -1 : 1;
    }
    if (aValue > bValue) {
        return sortDirection === "asc" ? 1 : -1;
    }
    return 0;
}

/**
 * Sorts books by the specified column and direction
 */
export function sortBooks(
    books: BookWithStats[],
    sortColumn: SortColumn,
    sortDirection: "asc" | "desc"
): BookWithStats[] {
    return [...books].sort((a, b) => {
        const aValue = getSortValue(a, sortColumn);
        const bValue = getSortValue(b, sortColumn);

        // Handle null values (featuredDate and rating can be null, createdAt cannot)
        const aIsNull = isNullValue(aValue, sortColumn);
        const bIsNull = isNullValue(bValue, sortColumn);

        if (aIsNull) {
            return 1; // nulls go to end
        }
        if (bIsNull) {
            return -1; // nulls go to end
        }

        return compareValues(aValue, bValue, sortDirection);
    });
}

/**
 * Applies book filters based on active filter types
 */
export function applyBookFilters(books: BookWithStats[], activeFilters: Set<FilterType>): BookWithStats[] {
    if (activeFilters.size === 0) {
        return books;
    }

    return books.filter((book) => {
        // Any active filter must match (OR logic)
        for (const filterType of activeFilters) {
            let matches = false;

            switch (filterType) {
                case "alreadyUsed":
                    matches = isScheduledBook(book);
                    break;
                case "neverUsed":
                    matches = !isScheduledBook(book);
                    break;
            }

            if (matches) {
                return true; // Include if ANY filter matches
            }
        }

        return false; // Exclude if NO filters match
    });
}

/**
 * Gets filter counts for each filter type
 */
export function getFilterCounts(books: BookWithStats[]): Record<FilterType, number> {
    return {
        alreadyUsed: books.filter((book) => isScheduledBook(book)).length,
        neverUsed: books.filter((book) => !isScheduledBook(book)).length,
    };
}
